import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { BookOpen, FileCheck, Loader2 } from 'lucide-react';

const WriterOverview = () => {
  const [stats, setStats] = useState(null);
  const [recentBooks, setRecentBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, booksRes] = await Promise.all([
        axios.get(`${API}/stats/writer`),
        axios.get(`${API}/books/my-submissions`)
      ]);
      setStats(statsRes.data);
      setRecentBooks(booksRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Oorsig
        </h2>
        <p className="text-gray-600">Welkom terug! Hier is jou statistieke.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          icon={<BookOpen className="h-8 w-8" />}
          title="Totale Boeke"
          value={stats?.total_books || 0}
          color="from-orange-400 to-orange-600"
        />
        <StatCard
          icon={<FileCheck className="h-8 w-8" />}
          title="Gepubliseer"
          value={stats?.published_books || 0}
          color="from-green-400 to-green-600"
        />
        <StatCard
          icon={<Loader2 className="h-8 w-8" />}
          title="In Proses"
          value={stats?.in_progress || 0}
          color="from-blue-400 to-blue-600"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Onlangse Indienings</CardTitle>
          <CardDescription>Jou mees onlangse boek indienings</CardDescription>
        </CardHeader>
        <CardContent>
          {recentBooks.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Geen indienings nog nie</p>
          ) : (
            <div className="space-y-4">
              {recentBooks.map((book) => (
                <div key={book.id} className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-900">{book.boek_naam}</h4>
                    <p className="text-sm text-gray-600">{book.genre} • {book.word_count} woorde</p>
                  </div>
                  <StatusBadge status={book.status} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const StatCard = ({ icon, title, value, color }) => {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 bg-gradient-to-br ${color} rounded-2xl flex items-center justify-center text-white`}>
            {icon}
          </div>
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    'Wag vir Proeflees': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Wag vir Proeflees' },
    'Besig met Proeflees': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Besig met Proeflees' },
    'Wag vir Regmaak': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Wag vir Regmaak' },
    'Wag vir Goedkeuring': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Wag vir Goedkeuring' },
    'Gereed vir Publikasie': { bg: 'bg-green-100', text: 'text-green-800', label: 'Gereed vir Publikasie' },
    'Gepubliseer': { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Gepubliseer' },
  };

  const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

export default WriterOverview;
export { StatusBadge };